import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Lightbulb, Users, Award, Clock, Globe, CheckCircle, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const milestones = [
  { year: '2015', event: { de: 'Gründung von e-Matrix', en: 'e-Matrix founded' } },
  { year: '2017', event: { de: 'Erste 10 Kunden', en: 'First 10 clients' } },
  { year: '2019', event: { de: 'Launch von ematrixsys', en: 'ematrixsys platform launch' } },
  { year: '2021', event: { de: '50+ E-Learning Module', en: '50+ e-learning modules' } },
  { year: '2024', event: { de: '200+ zufriedene Kunden', en: '200+ happy clients' } }
];

export default function AboutPage() {
  const { language, t } = useLanguage();

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      <SEO
        title={language === 'de' ? 'Über uns' : 'About Us'}
        description={language === 'de' ? 'e-Matrix Innovations GmbH - Ihr Partner für Corporate E-Learning seit 2015. Erfahren Sie mehr über unser Team und unsere Mission.' : 'e-Matrix Innovations GmbH - Your partner for corporate e-learning since 2015. Learn more about our team and mission.'}
        path="/about"
        lang={language}
      />
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#E7F0FA] via-white to-[#dde9f5]" data-testid="about-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <span className="text-sm font-semibold tracking-wide text-[#2E5E99] mb-4 block">
              {t('about.hero.subtitle')}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0D2440] mb-6">
              {t('about.hero.title')}
            </h1>
            <p className="text-lg md:text-xl text-[#5A7A9B] max-w-2xl mx-auto">
              {t('about.hero.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section - Without image */}
      <section className="py-16 md:py-20 bg-white" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <Target className="w-12 h-12 text-[#2E5E99] mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-[#0D2440] mb-6">
              {t('about.mission.title')}
            </h2>
            <p className="text-lg text-[#5A7A9B] max-w-2xl mx-auto">
              {t('about.mission.description')}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { de: 'Einfache Implementierung', en: 'Easy implementation' },
              { de: 'Messbare Ergebnisse', en: 'Measurable results' },
              { de: 'Zufriedene Mitarbeiter', en: 'Satisfied employees' },
              { de: 'Rechtssicherheit', en: 'Legal compliance' }
            ].map((item, index) => (
              <motion.div
                key={item.de}
                className="flex items-center space-x-2 p-4 bg-[#E7F0FA] rounded-xl"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                <span className="text-[#0D2440] text-sm font-medium">{item[language]}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-20 bg-[#E7F0FA]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
              {t('about.values.title')}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { key: 'quality', icon: Award },
              { key: 'innovation', icon: Lightbulb },
              { key: 'partnership', icon: Users }
            ].map((value, index) => (
              <motion.div
                key={value.key}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow border border-[#C8D8E8]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`value-${value.key}`}
              >
                <div className="w-14 h-14 bg-[#2E5E99]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-[#2E5E99]" />
                </div>
                <h3 className="font-semibold text-lg text-[#0D2440] mb-2">
                  {t(`about.values.items.${value.key}.title`)}
                </h3>
                <p className="text-[#5A7A9B] text-sm">
                  {t(`about.values.items.${value.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section - Compact */}
      <section className="py-16 md:py-20 bg-white" data-testid="timeline-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
              {language === 'de' ? 'Unsere Reise' : 'Our Journey'}
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#C8D8E8] md:-translate-x-1/2" />

            {milestones.map((milestone, index) => (
              <motion.div
                key={milestone.year}
                className={`relative flex items-center mb-8 last:mb-0 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-10 md:text-right' : 'md:pl-10'}`}>
                  <div className="inline-flex items-center space-x-3 bg-[#E7F0FA] rounded-lg px-4 py-3 border border-[#C8D8E8]">
                    <span className="text-xl font-bold text-[#2E5E99]">{milestone.year}</span>
                    <span className="text-[#0D2440]">{milestone.event[language]}</span>
                  </div>
                </div>
                <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-3 h-3 bg-[#2E5E99] rounded-full border-2 border-white shadow" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-20 bg-[#0D2440]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '100+', label: language === 'de' ? 'Lösungen' : 'Solutions', icon: Award },
              { value: '80+', label: language === 'de' ? 'Personenjahre' : 'Individual Years of Experience', icon: Briefcase },
              { value: '200+', label: language === 'de' ? 'Kunden' : 'Clients', icon: Users },
              { value: '10+', label: language === 'de' ? 'Sprachen' : 'Languages', icon: Globe }
            ].map((stat, index) => (
              <motion.div
                key={stat.value}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <stat.icon className="w-8 h-8 text-[#2E5E99] mx-auto mb-3" />
                <div className="text-3xl md:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-white/70 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-[#2E5E99]" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {language === 'de'
                ? 'Werden Sie Teil unserer Erfolgsgeschichte'
                : 'Become Part of Our Success Story'}
            </h2>
            <p className="text-white/80 mb-8">
              {language === 'de'
                ? 'Kontaktieren Sie uns und erfahren Sie, wie wir Ihr Unternehmen unterstützen können.'
                : 'Contact us and discover how we can support your company.'}
            </p>
            <Link to="/contact" data-testid="about-contact-cta">
              <Button className="bg-white text-[#2E5E99] hover:bg-white/90 h-12 px-8 text-base">
                {language === 'de' ? 'Kontakt aufnehmen' : 'Get in Touch'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
